import {
    PersonOutline, Person, BusinessCenter,
    HomeOutlined, ExitToApp,
    ChevronLeft, BusinessCenterOutlined,
    BookmarkBorderOutlined, DescriptionOutlined, KeyboardArrowDown,
    SettingsOutlined, WorkOutline, BoltOutlined,
} from '@mui/icons-material';

import {
    Fragment, memo, type MouseEvent, useContext, useEffect, useState,
} from 'react';

import {
    Container, Popover,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LoggedInUser } from '../../../../types/auth.types';
import UserContext from '../../context/user.context';
import Anchor from '../../foundation/Anchor';
import Button from '../../foundation/Button/Button';
import Logo from '../../foundation/Logo';
import LoginModal from '../../modals/LoginModal/LoginModal';
import ResetPasswordModal from '../../modals/ResetPasswordModal/ResetPasswordModal';
import RegisterModal from '../../modals/RegisterModal/RegisterModal';
import { removeURLParameter } from '../../../utils/urlUtils';
import NavbarMobile from './NavbarMobile';
import CustomBR from '../../foundation/CustomBR';
import ExpressDialogs from '../../components/ExpressDialogs/ExpressDialogs';
import {
    NavbarContainer,
    DesktopNavbar,
    DesktopNavbarWrapper,
    DesktopContainer,
    MobileContainer,
    AuthButtons,
    LogoContainer,
    LogoDivider,
    ExpressBadge,
    ExpressBadgeGroup,
    ExpressLogoWrapper,
    RecruitButton,
    NavItemStyled,
    SubMenuStyle,
    AccountMenuChevron,
    AccountMenuDivider,
    AccountMenuItem,
    AccountMenuList,
    AccountMenuTrigger,
    AccountPopover,
} from './Navbar.styles';

export type NavbarProps = {
    logoutClick: (ev?: MouseEvent) => void
    showExpressBadge?: boolean
}

type NavSubItem = {
    text: string
    url?: string
    iconEl?: JSX.Element
    onClick?: (ev?: MouseEvent) => void
    dividerBefore?: boolean
}

type NavItem = {
    onClick?: (ev?: MouseEvent) => void
    text: string,
    url?: string,
    icon?: string,
    iconEl?: JSX.Element,
    accountMenu?: boolean,
    navItems?: NavSubItem[]
}

const expressMenuItem: NavItem = {
    text: 'אמפלוי אקספרס',
    url: '/resume-landing-page',
    iconEl: <BoltOutlined />,
};

const homeMenuItem: NavItem = {
    text: 'דף הבית',
    url: '/',
    iconEl: <HomeOutlined />,
};

const employerMenuItem: NavItem = {
    text: 'כניסת מעסיקים',
    iconEl: <BusinessCenterOutlined />,
    url: '/backoffice',
};

const accountNavItems: NavSubItem[] = [{
    text: 'הפרופיל שלי',
    url: '/user/profile',
    iconEl: <PersonOutline />,
}, {
    text: 'ניהול קורות חיים',
    url: '/user/cvs',
    iconEl: <DescriptionOutlined />,
}, {
    text: 'משרות שפניתי אליהן',
    url: '/user/appliedJobs',
    iconEl: <WorkOutline />,
}, {
    text: 'המשרות השמורות שלי',
    url: '/user/savedJobs',
    iconEl: <BookmarkBorderOutlined />,
}, {
    text: 'הגדרות וסוכן חכם',
    url: '/user/settings',
    iconEl: <SettingsOutlined />,
}];

const getAccountMenuItem = (
    logoutClick: NavbarProps['logoutClick'],
    includeLogout: boolean,
): NavItem => ({
    text: 'החשבון שלי',
    iconEl: <PersonOutline />,
    accountMenu: true,
    navItems: includeLogout ? [...accountNavItems, {
        text: 'התנתקות',
        iconEl: <ExitToApp />,
        onClick: logoutClick,
        dividerBefore: true,
    }] : accountNavItems,
});

const getMenu = (user: LoggedInUser | undefined, logoutClick: NavbarProps['logoutClick']): NavItem[] => (
    user
        ? [expressMenuItem, homeMenuItem, employerMenuItem, getAccountMenuItem(logoutClick, true)]
        : [expressMenuItem, homeMenuItem]
);

const getMobileMenu = (user: LoggedInUser | undefined, logoutClick: NavbarProps['logoutClick']): NavItem[] => (
    user
        ? [
            expressMenuItem,
            homeMenuItem,
            getAccountMenuItem(logoutClick, false),
            employerMenuItem,
            {
                text: 'התנתקות',
                iconEl: <ExitToApp />,
                onClick: logoutClick,
            },
        ]
        : [expressMenuItem, homeMenuItem]
);

export type NavItemProps = NavItem

const NavItemDesktop = (props: NavItemProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const theme = useTheme();

    const isMdDown = useMediaQuery(theme.breakpoints.down('md'));

    if (props.accountMenu) {
        const accountMenuId = 'account-menu-popover';
        const horizontalOrigin = theme.direction === 'rtl' ? 'left' : 'right';
        return <NavItemStyled>
            <AccountMenuTrigger
                type="button"
                aria-controls={anchorEl ? accountMenuId : undefined}
                aria-expanded={!!anchorEl}
                aria-haspopup="menu"
                onClick={(ev: MouseEvent<HTMLElement>) => setAnchorEl(ev.currentTarget)}
            >
                {props.iconEl}
                <span>{props.text}</span>
                <AccountMenuChevron $open={!!anchorEl}>
                    <KeyboardArrowDown />
                </AccountMenuChevron>
            </AccountMenuTrigger>
            <AccountPopover
                id={accountMenuId}
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: horizontalOrigin }}
                transformOrigin={{ vertical: 'top', horizontal: horizontalOrigin }}
            >
                <AccountMenuList role="menu">
                    {props.navItems?.map(item => <Fragment key={item.text}>
                        {item.dividerBefore && <AccountMenuDivider />}
                        {item.url ? <AccountMenuItem
                            component="a"
                            href={item.url}
                            role="menuitem"
                            onClick={() => setAnchorEl(null)}
                        >
                            {item.iconEl}
                            <span>{item.text}</span>
                        </AccountMenuItem> : <AccountMenuItem
                            role="menuitem"
                            onClick={(ev: MouseEvent<HTMLButtonElement>) => {
                                setAnchorEl(null);
                                item.onClick?.(ev);
                            }}
                        >
                            {item.iconEl}
                            <span>{item.text}</span>
                        </AccountMenuItem>}
                    </Fragment>)}
                </AccountMenuList>
            </AccountPopover>
        </NavItemStyled>;
    }

    return <NavItemStyled>
        {props.iconEl && isMdDown && props.iconEl}
        <Anchor onClick={(ev: MouseEvent<HTMLElement>) => {
            if (props.navItems) setAnchorEl(ev.currentTarget);
            else if (props.onClick) props.onClick(ev);
        }} variant="subtitle1" text={props.text + (props.navItems ? ' ▾' : '')} href={props.url} />
        {props.navItems && <Popover
            open={!!anchorEl}
            anchorEl={anchorEl}
            elevation={0}
            onClose={() => setAnchorEl(null)}
            PaperProps={{
                style: {
                    background: 'none',
                    marginTop: '12px',
                    boxShadow: 'none',
                },
            }}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
        >
            <SubMenuStyle>
                {props.navItems.map(item => <div key={item.text}>
                    <Anchor variant="subtitle1" text={item.text} href={item.url} />
                    <ChevronLeft />
                </div>)}
            </SubMenuStyle>
        </Popover> }
    </NavItemStyled>;
};

const NavItemMobile = (props: NavItemProps): JSX.Element => {
    const isMdDown = useMediaQuery(theme => theme.breakpoints.down('md'));

    return <NavItemStyled>
        {props.iconEl && isMdDown && props.iconEl}
        <Anchor onClick={props.onClick} variant="subtitle1" text={props.text + (props.navItems ? ' ▾' : '')} href={props.url} />
        {props.navItems && <SubMenuStyle>
            {props.navItems.map(item => <div key={item.text} className={item.dividerBefore ? 'with-divider' : undefined}>
                {item.iconEl}
                <Anchor
                    variant="subtitle1"
                    text={item.text}
                    href={item.url || '#'}
                    onClick={(ev: MouseEvent<HTMLElement>) => {
                        if (!item.onClick) return;
                        ev.preventDefault();
                        item.onClick(ev);
                    }}
                />
                {item.url && <ChevronLeft />}
            </div>)}
        </SubMenuStyle>}
    </NavItemStyled>;
};

const Nabvar = (props: NavbarProps): JSX.Element => {
    const user = useContext(UserContext);
    const [loginModal, setLoginModal] = useState<boolean>(false);
    const [resetPasswordModal, setResetPasswordModal] = useState<boolean>(false);
    const [registerModal, setRegisterModal] = useState<boolean>(false);
    const [showSmartAgentRegisterModal, setShowSmartAgentRegisterModal] = useState<boolean>(true);
    const [employerRegisterModal, setEmployerRegisterModal] = useState<boolean>(false);

    useEffect(() => {
        window.modals = {
            ...(window.modals || {}),
            toggleLoginModal: () => setLoginModal(prev => !prev),
            toggleRegisterModal: ({ hideSmartAgent } = {}) => {
                setRegisterModal(prev => {
                    const showRegisterModal = !prev;
                    if (showRegisterModal) { setShowSmartAgentRegisterModal(!hideSmartAgent); }
                    return showRegisterModal;
                });
            },
            toggleEmployerRegisterModal: () => setEmployerRegisterModal(prev => !prev),
        };
    }, []);

    const onLoginClick = () => setLoginModal(true);
    const onRegisterClick = () => { setShowSmartAgentRegisterModal(true); setRegisterModal(true); };
    const onEmployerRegisterClick = () => {
        window.location.assign('/backoffice/login');
    };

    const renderExpressBadge = () => props.showExpressBadge && <ExpressBadgeGroup>
        <ExpressBadge>Express</ExpressBadge>
        <LogoDivider />
    </ExpressBadgeGroup>;

    const renderLogo = () => (props.showExpressBadge
        ? <ExpressLogoWrapper><Logo /></ExpressLogoWrapper>
        : <Logo />);

    return <Container>
        <ExpressDialogs user={user} />
        <LoginModal
            gotoRegister={() => { setLoginModal(false); setRegisterModal(true); }}
            onResetPasswordClick={() => { setLoginModal(false); setResetPasswordModal(true); }}
            onClose={() => setLoginModal(false)} open={loginModal} />
        <RegisterModal
            showSmartAgent={showSmartAgentRegisterModal}
            gotoLogin={() => { setRegisterModal(false); setLoginModal(true); }}
            onClose={() => setRegisterModal(false)} open={registerModal} />
        <RegisterModal
            isEmployer
            welcomeText={`
                מעסיק? הגעת למקום הנכון, ב-Mploy פרסום משרה מתבצע בכמה קליקים.
                תחילה יש לבצע הרשמה קצרה - זה פשוט וקל! מיד לאחר מכן תוכל לפרסם משרות.
            `}
            showSmartAgent={false}
            gotoLogin={() => { setEmployerRegisterModal(false); setLoginModal(true); }}
            onClose={() => {
                setEmployerRegisterModal(false);
                window.history.pushState({}, '', removeURLParameter('redirect'));
            }} open={employerRegisterModal} />
        <ResetPasswordModal onClose={() => setResetPasswordModal(false)} open={resetPasswordModal} />
        <NavbarContainer>
            {/* Desktop Navigation */}
            <DesktopContainer display={{ xs: 'none', md: 'flex' }}>
                <LogoContainer>
                    {renderExpressBadge()}
                    {renderLogo()}
                </LogoContainer>

                <DesktopNavbarWrapper>
                    <DesktopNavbar>
                        {getMenu(user, props.logoutClick).map(navItem => <NavItemDesktop {...navItem} key={navItem.text} />)}
                    </DesktopNavbar>

                    {!user && <AuthButtons>
                        <Button
                            onClick={onLoginClick}
                            size="medium"
                            color="secondary"
                            variant="outlined"
                            startIcon={<Person />}
                        >
                            התחברות
                        </Button>
                        <Button
                            onClick={onRegisterClick}
                            size="medium"
                            color="primary"
                            startIcon={<PersonOutline />}
                        >
                            הרשמה
                        </Button>
                        <RecruitButton>
                            <Button
                                onClick={onEmployerRegisterClick}
                                size="medium"
                                color="primary"
                                variant="contained"
                                startIcon={<BusinessCenter />}
                            >
                                כניסת מעסיקים
                            </Button>
                        </RecruitButton>
                    </AuthButtons>}
                </DesktopNavbarWrapper>
            </DesktopContainer>

            {/* Mobile Navigation */}
            <MobileContainer display={{ xs: 'flex', md: 'none' }}>
                <NavbarMobile>
                    <>
                        {getMobileMenu(user, props.logoutClick)
                            .map(navItem => <NavItemMobile {...navItem} key={navItem.text} />)}
                        {!user ? <>
                            <CustomBR />
                            <Button
                                fullWidth
                                onClick={onLoginClick}
                                size="large"
                                color="secondary"
                                startIcon={<Person />}
                            >
                                התחברות
                            </Button>
                            <CustomBR times={2} />
                            <Button
                                fullWidth
                                onClick={onRegisterClick}
                                size="large"
                                color="primary"
                                startIcon={<PersonOutline />}
                            >
                                הרשמה
                            </Button>
                            <CustomBR times={2} />
                            <Button
                                fullWidth
                                onClick={onEmployerRegisterClick}
                                size="large"
                                color="primary"
                                variant="contained"
                                startIcon={<BusinessCenter />}
                            >
                                כניסת מעסיקים
                            </Button>
                        </> : null}
                    </>
                </NavbarMobile>
                <LogoContainer>
                    {renderExpressBadge()}
                    {renderLogo()}
                </LogoContainer>
            </MobileContainer>
        </NavbarContainer>
    </Container>;
};

export default memo(Nabvar);
